import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Heading, HStack, IconButton, Radio, RadioGroup, Stack, Table, Tbody, Td, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { ArrowBackIcon, RepeatIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import rbacService from "../services/rbacService";
import type { Menu, UserMenuOverride } from "../../rbac/types";

// Tri-state per menu: inherit (no override), allow (has_access=true), deny (has_access=false)

type OverrideState = "inherit" | "allow" | "deny";

const UserOverridesPage: React.FC = () => {
  const { id } = useParams();
  const userId = id as string; // UUID
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [overrides, setOverrides] = useState<Record<string, OverrideState>>({});

  const load = async () => {
    setLoading(true);
    try {
      const [allMenus, userOverrides] = await Promise.all([
        rbacService.getMenus({ skip: 0, limit: 1000 }),
        rbacService.getUserOverrides(userId),
      ]);
      setMenus(allMenus);
      const map: Record<string, OverrideState> = {};
      userOverrides.forEach((ov: UserMenuOverride) => {
        map[ov.menu_id] = ov.has_access ? "allow" : "deny";
      });
      setOverrides(map);
    } catch (e: any) {
      toast({ status: "error", title: "Gagal memuat overrides", description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [userId]);

  const updateState = async (menuId: string, state: OverrideState) => {
    try {
      if (state === "inherit") {
        // Remove override
        if (overrides[menuId] !== undefined) {
          await rbacService.deleteUserOverride(userId, menuId);
        }
      } else {
        await rbacService.createOrUpdateOverride(userId, { menu_id: menuId, has_access: state === "allow" });
      }
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Gagal mengubah override", description: e?.response?.data?.detail || e?.message });
    }
  };

  return (
    <Box p={4}>
      <HStack justify="space-between" mb={4}>
        <HStack>
          <Button leftIcon={<ArrowBackIcon />} variant="ghost" onClick={() => navigate(-1)}>Back</Button>
          <Heading size="md">User Overrides</Heading>
        </HStack>
        <IconButton aria-label="refresh" icon={<RepeatIcon />} onClick={() => load()} isLoading={loading} />
      </HStack>

      <Table size="sm" variant="striped">
        <Thead>
          <Tr>
            <Th>Menu</Th>
            <Th>Path</Th>
            <Th>Override</Th>
          </Tr>
        </Thead>
        <Tbody>
          {menus.map((m) => {
            const state: OverrideState = overrides[m.id] ?? "inherit";
            return (
              <Tr key={m.id}>
                <Td>{m.display_name || m.menu_key}</Td>
                <Td>{m.path}</Td>
                <Td>
                  <RadioGroup value={state} onChange={(val) => updateState(m.id, val as OverrideState)}>
                    <Stack direction="row" spacing={4}>
                      <Radio value="inherit">Inherit</Radio>
                      <Radio value="allow" colorScheme="green">Allow</Radio>
                      <Radio value="deny" colorScheme="red">Deny</Radio>
                    </Stack>
                  </RadioGroup>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

export default UserOverridesPage;
